import { AggregatedTargetAttribute, AggregatedTargetAttributeDisplayNames, getFHWAClassDisplayName, getLaneDisplayName, getTargetTypeDisplayName, getTurnTypeDisplayName } from "@app/shared";
import { addDays, addMilliseconds, addMonths, endOfWeek, getDate, getMonth, getYear, startOfWeek } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { useContext, useMemo } from "react";
import useRenderingTrace from "../../hooks/useRenderingTrace";
import { DateInput, IconLink, Icons, PageContainer, Select, SelectOptionGroup } from "../shared";
import { HoverTooltip } from "../shared/HoverTooltip";
import { MultiSelect, SimpleOption } from "../shared/inputs/MultiSelect";
import { DateOptions, IConfiguredTrafficReport } from "./IConfiguredTrafficReport";
import { GetReport, GroupedReportConfigurations } from "./ReportComponents";
import { AllFHWAClasses, AllLanes, AllTargetTypes, AllTurns, OrgReportContext, ReportContext } from "./reportHelpers";
import { TargetTypeMultiSelect } from "./TargetTypeMultiSelect";

const reportGroups: SelectOptionGroup[] = GroupedReportConfigurations.map(g => {
    return {
        label: g.label,
        options: g.values.map(rc => { return { label: rc.name, value: rc.key, disabled: rc.config?.disabled } })
    }
});

export function getReportPeriod(selectedDateId: DateOptions, facilityTimezone: string) {//tested working 8/16/2024

    const current = new Date();

    let updatedPeriod: { inclusiveStart: Date | undefined, exclusiveEnd: Date | undefined } = {
        inclusiveStart: undefined,
        exclusiveEnd: undefined
    };
    switch (selectedDateId) {
        case DateOptions.AllData:
            updatedPeriod = {
                inclusiveStart: new Date(2020, 0, 1),//new Date(`${utcToZonedTime(current, facilityTimezone).getFullYear() + 1}-01-01T00:00${fLocalOffset}`),
                exclusiveEnd: new Date(current.getFullYear() + 1, 0, 1)
            };
            break;
        case DateOptions.LastYear:
            updatedPeriod = {
                inclusiveStart: new Date(current.getFullYear() - 1, 0, 1),
                exclusiveEnd: new Date(current.getFullYear(), 0, 1)
            };
            break;
        case DateOptions.LastMonth:
            current.setDate(0);//last day of last month
            current.setDate(1);//first day of last month

            updatedPeriod = {
                inclusiveStart: new Date(current.getFullYear(), current.getMonth(), current.getDate()),
                exclusiveEnd: addMonths(new Date(current.getFullYear(), current.getMonth(), current.getDate()), 1)
            };
            break;
        case DateOptions.LastWeek:
            current.setDate(current.getDate() - (current.getDay() + 7)); // First day is the day of the month - the day of the week
            updatedPeriod = {
                inclusiveStart: new Date(current.getFullYear(), current.getMonth(), current.getDate()),
                exclusiveEnd: addDays(new Date(current.getFullYear(), current.getMonth(), current.getDate()), 7)
            };
            break;
        case DateOptions.Last90Days:
            const daysAgo90 = new Date(addDays(new Date(), -89).setHours(0, 0, 0, 0));
            const startOfDay = current.setHours(0, 0, 0, 0);
            updatedPeriod = {
                inclusiveStart: daysAgo90,
                exclusiveEnd: addDays(startOfDay, 1)
            };
            break;
        case DateOptions.ThisYear:
            updatedPeriod = {
                inclusiveStart: new Date(current.getFullYear(), 0, 1),
                exclusiveEnd: new Date(current.getFullYear() + 1, 0, 1)
            };
            break;
        case DateOptions.ThisMonth:
            updatedPeriod = {
                inclusiveStart: new Date(current.getFullYear(), current.getMonth(), 1),
                exclusiveEnd: new Date(current.getFullYear(), current.getMonth() + 1, 1)
            };
            break;
        case DateOptions.ThisWeek:
            updatedPeriod = {
                inclusiveStart: startOfWeek(current, { weekStartsOn: 0 }), // 0 = Sunday,
                exclusiveEnd: addMilliseconds(endOfWeek(current, { weekStartsOn: 0 }), 1)
            };
            break;
        default:
            break;
    }
    if (updatedPeriod.inclusiveStart) {
        updatedPeriod.inclusiveStart = zonedTimeToUtc(updatedPeriod.inclusiveStart, facilityTimezone)!;
    }
    if (updatedPeriod.exclusiveEnd) {
        updatedPeriod.exclusiveEnd = zonedTimeToUtc(updatedPeriod.exclusiveEnd, facilityTimezone)!;
    }
    return updatedPeriod;
}

export const ReportParametersForm: React.FC = () => {
    const { reportConfig, updateReportConfig, isLoading } = useContext(ReportContext);
    const { facilities: contextFacilities } = useContext(OrgReportContext);
    const { facility, device } = reportConfig.context;
    const deviceOptions = useMemo(() => !!!facility ? [{ label: device?.name, value: device?.id }] : [{ label: 'All Devices', value: 'all' }, ...facility?.devices?.map(d => ({ label: d.name, value: d.id! })) ?? []], [device, facility]);
    const deviceSelections = reportConfig.deviceIds?.length === (deviceOptions.length - 1) ? ['all'] : reportConfig.deviceIds;
    const showFilters = true;


    function handlePeriodChange(e: any) {
        const selectedDateId = e.target!.value as DateOptions;
        updateReportConfig({ ...reportConfig!, ...getReportPeriod(selectedDateId, reportConfig!.chartTimezone), selectedDateId: selectedDateId ?? DateOptions.Custom });
    }
    function handleGroupingChange(grouping: AggregatedTargetAttribute, selectedVals?: Array<number>) {
        if (grouping !== reportConfig.grouping) {
            selectedVals = getAllValues(grouping)
        }
        const reportVals = selectedVals ?? [];
        updateReportConfig({ ...reportConfig, grouping: grouping, groupingValues: reportVals });
    }

    const groupOptions = useMemo(() => {
        const grouping = reportConfig.grouping;

        let options: SimpleOption[];
        if (grouping === AggregatedTargetAttribute.FHWAClass) {
            options = AllFHWAClasses.map(x => ({ label: getFHWAClassDisplayName(x), value: x }) as SimpleOption);
        } else if (grouping === AggregatedTargetAttribute.TargetType) {
            options = AllTargetTypes.map(x => ({ label: getTargetTypeDisplayName(x), value: x }) as SimpleOption);
        } else if (grouping === AggregatedTargetAttribute.Lane) {
            options = AllLanes.map(x => ({ label: getLaneDisplayName(x) === '' ? "Unknown" : getLaneDisplayName(x), value: x }) as SimpleOption);
        } else if (grouping === AggregatedTargetAttribute.Turn) {
            options = AllTurns.map(x => ({ label: getTurnTypeDisplayName(x), value: x }) as SimpleOption);
        } else {
            return [];
        }
        return options.sort((a, b) => (a.label ?? "").toString().localeCompare((b.label ?? "").toString(), undefined, { numeric: true }));
    }, [reportConfig.grouping]);

    function getAvailableDevices(): Array<string> {
        if (facility) {
            return facility.devices?.map(d => d.id ?? '') ?? [];
        }
        return [device!.id!];
    }

    const selectedReportGroup = useMemo(() => GroupedReportConfigurations.find(rc => rc.values.find(x => x.key === reportConfig.selectedReportId)), [reportConfig.selectedReportId]);
    const selectedReport = useMemo(() => {
        const group = GroupedReportConfigurations.find(rc => rc.values.find(x => x.key === reportConfig.selectedReportId));
        return group?.values.find(x => x.key === reportConfig.selectedReportId)
    }, [reportConfig.selectedReportId]);


    function handleCustomStart(e: Date | null) {
        if (!e) return;
        let start = new Date(e);
        start.setHours(0, 0, 0, 0);//now beginning of the day in browser time
        start = zonedTimeToUtc(start.toISOString(), reportConfig!.chartTimezone);
        updateReportConfig({ ...reportConfig!, inclusiveStart: start, selectedDateId: calcRangeSelection(start, reportConfig!.exclusiveEnd) });
    }
    function handleCustomEnd(e: Date | null) {
        if (!e) return;
        let end = addDays(new Date(e), 1);
        end.setHours(0, 0, 0, 0);
        end = zonedTimeToUtc(end.toISOString(), reportConfig!.chartTimezone)!;
        updateReportConfig({ ...reportConfig!, exclusiveEnd: end, selectedDateId: calcRangeSelection(reportConfig!.inclusiveStart, end) });
    }
    function handleDevicesChange(e: any) {// Array<string | number>) {
        var sIds = e as string[];
        updateReportConfig({ ...reportConfig, deviceIds: (sIds)[0] === 'all' ? getAvailableDevices() : sIds });
    }
    const numFilters = useMemo(() => {
        let count = 0;
        if (reportConfig!.deviceIds !== undefined && reportConfig!.deviceIds?.length !== (deviceOptions.length - (deviceOptions[0].value === 'all' ? 1 : 0))) count++;
        if (reportConfig!.groupingValues.length !== getAllValues(reportConfig.grouping).length) count++; //TODO:
        if (reportConfig!.grouping !== AggregatedTargetAttribute.None) count++;
        if (selectedReport && reportConfig!.selectedDateId && (reportConfig!.selectedDateId !== selectedReport?.defaultRange)) count++;

        return count;
    }, [reportConfig, selectedReport, deviceOptions]);

    function resetFilters() {
        updateReportConfig({
            ...reportConfig!,
            chartType: selectedReport!.defaultChartType,
            ...getReportPeriod(selectedReport!.defaultRange, reportConfig!.chartTimezone),
            selectedDateId: selectedReport!.defaultRange,
            grouping: AggregatedTargetAttribute.None,
            groupingValues: [],
            deviceIds: getAvailableDevices()
        });
    } const handleReportChange = (e: React.ChangeEvent<HTMLSelectElement>) => changeReport(GetReport(e.target.value));

    function changeReport(report: IConfiguredTrafficReport) {

        updateReportConfig({
            ...reportConfig!,
            selectedReportId: report.key,
            chartType: report.defaultChartType,
            selectedDateId: report.defaultRange,
            ...getReportPeriod(report.defaultRange, reportConfig!.chartTimezone)
        });
    }
    function calcRangeSelection(start?: Date, end?: Date) {
        let result = DateOptions.Custom;
        if (start) {
            Object.values(DateOptions).forEach(opt => {
                var range = getReportPeriod(opt, reportConfig!.chartTimezone);
                if (range.inclusiveStart === start && range.exclusiveEnd === end) {
                    result = opt;
                }
            });
        }
        return result;
    }
    const dateSelectOptions = useMemo(() => {
        const maxDays = selectedReport?.config?.maxDaysRange;

        if (!maxDays) {
            return Object.values(DateOptions).map(opt => ({ label: opt, value: opt }));
        }

        return Object.values(DateOptions).filter(opt => {
            if (opt === DateOptions.Custom) return true;

            const range = getReportPeriod(opt, reportConfig!.chartTimezone);
            if (!range.inclusiveStart || !range.exclusiveEnd) return true;

            const daysDiff = Math.ceil((range.exclusiveEnd.getTime() - range.inclusiveStart.getTime()) / (1000 * 60 * 60 * 24));
            return daysDiff <= maxDays;
        }).map(opt => ({ label: opt, value: opt }));
    }, [selectedReport, reportConfig]);

    const groupingSelectOptions = Object.values(AggregatedTargetAttribute).filter(x => typeof x === 'number').map(opt => ({ label: AggregatedTargetAttributeDisplayNames[opt as number], value: opt }));

    if (selectedReport?.config && !selectedReport?.config?.allowTargetTypeGrouping && reportConfig.grouping !== AggregatedTargetAttribute.None) {
        updateReportConfig({ ...reportConfig, grouping: AggregatedTargetAttribute.None });
    }


    useRenderingTrace('ReportParametersForm', [facility, device, reportConfig]);

    const toInclusiveDateOnly = (x: Date) => {
        if (!x) { return x; }
        const dateOnly = convertToSystemDateOnly(x, reportConfig.chartTimezone);
        const p1 = zonedTimeToUtc(addDays(dateOnly, -1), reportConfig.chartTimezone);
        return p1;
    }

    const convertToSystemDateOnly = (date: Date, timeZone: string) => {
        const zonedDate = utcToZonedTime(date, timeZone);
        const year = getYear(zonedDate);
        const month = getMonth(zonedDate);
        const day = getDate(zonedDate);
        return new Date(year, month, day);
    }
    //const showFHWA = isSuperAdmin && reportConfig.selectedReportId == TotalVolumeByDay.key;

    return (
        <PageContainer Icon={Icons.StatsDots} title="Report Settings">
            <div className="flex flex-wrap justify-start">
                <div className="basis-full ">
                    <div className="flex flex-wrap">
                        <HoverTooltip placement="right" hoverable={
                            <Select disabled={(contextFacilities?.length ?? 0) > 0 || isLoading} className="w-full focus:outline-0 bg-transparent mb-4" label="Report" optionGroups={reportGroups} showGroupName={selectedReportGroup?.includeLabelInTitle} value={reportConfig.selectedReportId} onChange={handleReportChange} Icon={Icons.Info} />
                        }>
                            <span>{((contextFacilities?.length ?? 0) > 0) ? 'Navigate to specific location or device for additional report types' : selectedReport?.description}</span>
                        </HoverTooltip>
                        <span className="ml-5 mt-3 select-none flex items-center">
                            {numFilters !== 0 && <div className="w-5 h-5 ml-1 rounded-full bg-brand-primary flex items-center justify-center text-bright-text text-xs font-bold">{numFilters}</div>}
                            {numFilters !== 0 && <IconLink className="ml-9 text-info-icon" iconClassName="stroke-brand-primary reset-icon" Icon={Icons.Reset} onClick={resetFilters} >Reset</IconLink>}
                        </span>
                    </div>
                </div>
                {showFilters && <>
                    {(!!!device && selectedReport?.config?.hideDeviceSelection !== true) && <div className="hmm mr-5">
                        <MultiSelect disabled={isLoading} className="focus:outline-0 bg-transparent mb-4" label="Devices" options={deviceOptions as SimpleOption[]} value={deviceSelections!} onChange={handleDevicesChange} selectAllOption={deviceOptions[0] as SimpleOption} />
                    </div>}

                    <div className="hmm mr-5"><Select disabled={isLoading} className="focus:outline-0 bg-transparent mb-4" label="Data Range" options={dateSelectOptions} value={reportConfig.selectedDateId} onChange={handlePeriodChange} /></div>
                    {selectedReport?.config?.allowsCustomRange && reportConfig.selectedDateId === DateOptions.Custom && <div className="hmm mr-5"><DateInput disabled={isLoading} onChange={handleCustomStart} displayTimezone={reportConfig.chartTimezone} label="Start Date" showMonthYearPicker={false} showTimeSelect={false} ignoreStartDate={true} value={reportConfig.inclusiveStart?.toISOString()} showTimeInput={false} dateFormat="MMMM dd yyyy" /></div>}
                    {selectedReport?.config?.allowsCustomRange && reportConfig.selectedDateId === DateOptions.Custom && <div className="hmm mr-5"><DateInput disabled={isLoading} onChange={handleCustomEnd} displayTimezone={reportConfig.chartTimezone} label="End Date" showMonthYearPicker={false} showTimeSelect={false} ignoreStartDate={true} value={reportConfig.exclusiveEnd ? toInclusiveDateOnly(reportConfig.exclusiveEnd).toISOString() : undefined} minDate={reportConfig.inclusiveStart} showTimeInput={false} dateFormat="MMMM dd yyyy" /></div>}
                </>}
                {showFilters && selectedReport?.config?.allowTargetTypeGrouping && <div className="hmm mr-5"><Select disabled={isLoading} className="w-full focus:outline-0 bg-transparent mb-4" label="Target Grouping" options={groupingSelectOptions} value={reportConfig.grouping} onChange={e => handleGroupingChange(parseInt(e.currentTarget.value))} /></div>}
                {showFilters && reportConfig.grouping !== AggregatedTargetAttribute.None && <div className="hmm mr-5"><TargetTypeMultiSelect disabled={isLoading} className="min-w-[300px] mr-5" label="Included Values" onChange={e => handleGroupingChange(reportConfig.grouping, e as number[])} options={groupOptions} values={reportConfig.groupingValues} /></div>}
                {/* {showFilters && <div className="flex items-baseline mt-9 hmm mr-5">
                    {selectedReport?.config?.allowTargetTypeGrouping && <CheckboxInput label="Distinguish TargetTypes" className="mx-2 mr-5" onChange={e => updateReportConfig({ ...reportConfig, grouping: AggregatedTargetAttribute.TargetType })} checked={reportConfig.grouping !== AggregatedTargetAttribute.None} />}
                </div>} */}
            </div>
        </PageContainer>
    );
}

function getAllValues(grouping: AggregatedTargetAttribute) {
    if (grouping === AggregatedTargetAttribute.FHWAClass) {
        return AllFHWAClasses;
    } else if (grouping === AggregatedTargetAttribute.TargetType) {
        return AllTargetTypes;
    } else if (grouping === AggregatedTargetAttribute.Lane) {
        return AllLanes;
    } else if (grouping === AggregatedTargetAttribute.Turn) {
        return AllTurns;
    }
    return [];
}